import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Grid,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { get } from "src/utils/networkUtils";
import Iconify from "../iconify";
import PlayerCard from "./PlayerCard";

export default function UserCardsDialog({
  open,
  onClose,
  userId,
  username,
  onCardClick,
  positionFilter,
  isReplacingCard = false,
}) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selectedCards, setSelectedCards] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState("all");
  const limit = 100;

  const fetchUserCards = useCallback(
    async (reset = false) => {
      setLoading(true);
      setError(null);

      const currentOffset = reset ? 0 : offset;

      try {
        const response = await get(
          `/admin/users/${userId}/cards?offset=${currentOffset}&limit=${limit}`
        );

        if (response.cards) {
          if (reset) {
            setCards(response.cards);
            setOffset(limit);
          } else {
            setCards((prev) => [...prev, ...response.cards]);
            setOffset((prev) => prev + limit);
          }

          setHasMore(response.cards.length === limit);
        } else {
          if (reset) {
            setCards([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching user cards:", error);
        setError(error.response?.data?.error || "Failed to fetch user cards");
      } finally {
        setLoading(false);
      }
    },
    [userId, offset, limit]
  );

  const filteredCards =
    selectedPosition === "all"
      ? cards
      : cards.filter((card) => {
          const cardPosition = String(card.position);
          const filterPosition = String(selectedPosition);
          return (
            cardPosition === filterPosition ||
            (selectedPosition === "flex" &&
              (cardPosition === "15" ||
                cardPosition === "16" ||
                cardPosition === "13"))
          );
        });

  useEffect(() => {
    if (open && userId) {
      fetchUserCards(true);
    }
  }, [open, userId, fetchUserCards]);

  useEffect(() => {
    if (open) {
      setSelectedPosition(positionFilter);
    }
  }, [positionFilter, open]);

  const handleClose = () => {
    setCards([]);
    setError(null);
    setOffset(0);
    setHasMore(true);
    setSelectedCards([]);
    setIsSelectionMode(false);
    setSelectedPosition(positionFilter || "all");
    onClose();
  };

  const handleCardClick = (card) => {
    if (onCardClick && !isReplacingCard) {
      onCardClick(card);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchUserCards(false);
    }
  };

  const handleCardSelect = (card) => {
    if (!isSelectionMode) return;

    setSelectedCards((prev) => {
      const isAlreadySelected = prev.some((c) => c.cardId === card.cardId);

      if (isAlreadySelected) {
        return prev.filter((c) => c.cardId !== card.cardId);
      } else {
        if (prev.length >= 6) {
          return [...prev.slice(1), card];
        }
        return [...prev, card];
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="div">
              {username ? `${username}'s Cards` : "User Cards"}
            </Typography>
            {onCardClick && (
              <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                {isReplacingCard ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Updating lineup...
                  </>
                ) : (
                  "Click a card to replace it in the lineup"
                )}
              </Typography>
            )}
          </Box>
          {userId && (
            <Chip
              label={`User ID: ${userId}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        {cards.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {filteredCards.length} of {cards.length} cards
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Position</InputLabel>
              <Select
                value={selectedPosition}
                label="Position"
                onChange={(e) => setSelectedPosition(e.target.value)}
              >
                <MenuItem value="all">All Positions</MenuItem>
                <MenuItem value="0">Teams (T)</MenuItem>
                <MenuItem value="13">Center (C)</MenuItem>
                <MenuItem value="14">Defense (D)</MenuItem>
                <MenuItem value="15">Right Wing (RW)</MenuItem>
                <MenuItem value="16">Left Wing (LW)</MenuItem>
                <MenuItem value="flex">Flex (F)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && cards.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={400}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {cards.length === 0 && !loading && !error && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={400}
            sx={{ color: "text.secondary" }}
          >
            <Iconify icon="eva:inbox-outline" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">No Cards Found</Typography>
            <Typography variant="body2">
              This user doesn't have any cards yet.
            </Typography>
          </Box>
        )}

        {filteredCards.length > 0 && (
          <Grid container spacing={2}>
            {filteredCards.map((card, index) => (
              <Grid
                item
                xs={12}
                sm={3}
                md={2}
                lg={2}
                key={`${card.cardId}-${index}`}
              >
                <PlayerCard
                  card={card}
                  variant="detailed"
                  onClick={
                    !isReplacingCard ? () => handleCardClick(card) : undefined
                  }
                  onSelect={handleCardSelect}
                  isSelected={selectedCards.some(
                    (c) => c.cardId === card.cardId
                  )}
                  isSelectionMode={isSelectionMode}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* No filtered results */}
        {cards.length > 0 && filteredCards.length === 0 && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={400}
            sx={{ color: "text.secondary" }}
          >
            <Iconify icon="eva:search-outline" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">No Cards Found</Typography>
            <Typography variant="body2">
              No cards match the selected position filter.
            </Typography>
          </Box>
        )}

        {/* Load More Button */}
        {hasMore && cards.length > 0 && (
          <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Iconify icon="eva:arrow-down-outline" />
                )
              }
            >
              {loading ? "Loading..." : "Load More Cards"}
            </Button>
          </Box>
        )}

        {/* Selection Mode UI */}
        {isSelectionMode && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Selected Cards ({selectedCards.length}/6)
            </Typography>
            <Grid container spacing={2}>
              {selectedCards.map((card, index) => (
                <Grid
                  item
                  xs={12}
                  sm={3}
                  md={2}
                  lg={2}
                  key={`selected-${card.cardId}`}
                >
                  <PlayerCard
                    card={card}
                    variant="detailed"
                    isSelected={true}
                    isSelectionMode={true}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isReplacingCard}
          startIcon={<Iconify icon="eva:close-outline" />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
